import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyImport, LazyModule, registerLazyOrchestrator } from 'owa-bundling';
import markItemsAsReadStoreUpdate from 'owa-mail-actions/lib/triage/markItemsAsReadStoreUpdate';
import initializeExtendedCardForItemReadingPane from './actions/initializeExtendedCardForItemReadingPane';
import type { ResponseType, AttendanceType } from 'owa-graph-schema';
import type { ToggleUndoDarkModeActionMessage } from './actions/toggleUndoDarkMode';
import type ItemViewState from './store/schema/ItemViewState';
import type CalendarInlineComposeViewState from './store/schema/CalendarInlineComposeViewState';
import type { ActionSource } from 'owa-analytics-types';
import type { ClientItemId } from 'owa-client-ids';

export const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "ReadingPaneStore"*/ './lazyIndex'),
    { name: 'ReadingPaneStore' }
);

// Export delay loaded actions
export const lazyPrint: LazyAction<
    (
        itemId: ClientItemId,
        _actionSource: ActionSource,
        targetWindow: Window | undefined
    ) => Promise<void>,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.print);
export const lazyLoadItemReadingPane: LazyAction<
    typeof lazyIndex.loadItemReadingPane,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.loadItemReadingPane);
export const lazyLoadItemReadingPaneForSingleMailItemSelected: LazyAction<
    typeof lazyIndex.loadItemReadingPaneForSingleMailItemSelected,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.loadItemReadingPaneForSingleMailItemSelected);
export const lazyLoadItemState: LazyAction<
    typeof lazyIndex.loadItemState,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.loadItemState);

// Register lazy orchestrator
registerLazyOrchestrator(
    markItemsAsReadStoreUpdate,
    lazyModule,
    m => m.markItemsAsReadStoreUpdateOrchestrator
);
registerLazyOrchestrator(
    initializeExtendedCardForItemReadingPane,
    lazyModule,
    m => m.initializeExtendedCardForItemReadingPaneOrchestrator
);

export const lazyIsUnsupportedItem: LazyImport<
    typeof lazyIndex.isUnsupportedItem,
    LazyModule<typeof lazyIndex>
> = new LazyImport(lazyModule, m => m.isUnsupportedItem);

export const lazyReplyByMeeting: LazyAction<
    (
        referenceItemId: string,
        isCopilot: boolean,
        calendarInlineComposeViewState?: CalendarInlineComposeViewState | undefined,
        actionSource?: ActionSource | undefined,
        extractionEntityId?: string | undefined,
        internetMessageId?: string | undefined,
        sigsData?:
            | {
                  [key: string]: string;
              }
            | undefined,
        eventTimestamp?: number | undefined
    ) => Promise<string | null>,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.replyByMeeting);
export const lazyToggleUndoDarkMode: LazyAction<
    (viewState: ItemViewState) => ToggleUndoDarkModeActionMessage,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.toggleUndoDarkMode);
export const lazySetHidePopout: LazyAction<
    (value: boolean) => void,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setHidePopout);
export const lazySetCalendarEventResponseTypeMutator: LazyAction<
    (itemId: string, responseType: ResponseType, attendance: AttendanceType) => void,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.setCalendarEventResponseTypeMutator);
export const lazyCreateThreadSummarizationViewState: LazyAction<
    typeof lazyIndex.createThreadSummarizationViewState,
    LazyModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.createThreadSummarizationViewState);
// Export synchronous actions
export { isItemHeldByConversationItemParts, isItemHeldByItemReadingPane } from './utils/isItemHeld';

// Export store and schema
export type { default as ItemViewState } from './store/schema/ItemViewState';
export { default as readingPaneStore } from './store/Store';
export type { default as SmartPillViewState } from './store/schema/SmartPillViewState';
export type { default as ItemReadingPaneViewState } from './store/schema/ItemReadingPaneViewState';
export type { default as ItemPartViewState } from './store/schema/ItemPartViewState';
export type { default as DeeplinkId } from './store/schema/DeeplinkId';
export type { default as ActionableMessageCardInItemViewState } from './store/schema/ActionableMessageCardInItemViewState';
export { default as getConversationReadingPaneViewState } from './utils/getConversationReadingPaneViewState';
export { default as getItemReadingPaneViewState } from './utils/getItemReadingPaneViewState';
export type { default as LoadItemOptions } from './store/schema/LoadItemOptions';
export type { default as ReadingPaneViewState } from './store/schema/ReadingPaneViewState';
